import * as PIXI from 'pixi.js';
import type { Token } from '@machina/core';
import { TokenRenderer } from './TokenRenderer';
import { useTokenStore } from '../stores/tokenStore';

/**
 * TokenLayer
 *
 * Manages all token renderers on the map.
 * Syncs with Zustand tokenStore for state management.
 */
export class TokenLayer {
  public container: PIXI.Container;

  private renderers: Map<string, TokenRenderer> = new Map();
  private gridSize: number;
  private unsubscribe: (() => void) | null = null;

  constructor(gridSize: number) {
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;
    this.gridSize = gridSize;

    // Subscribe to store changes
    this.subscribeToStore();
  }

  /**
   * Subscribe to tokenStore changes
   */
  private subscribeToStore(): void {
    this.unsubscribe = useTokenStore.subscribe((state, prevState) => {
      // Check if tokens changed
      if (state.tokens !== prevState.tokens) {
        this.syncTokens(state.tokens);
      }
    });

    // Initial sync
    const { tokens } = useTokenStore.getState();
    this.syncTokens(tokens);
  }

  /**
   * Sync renderers with token state
   */
  private syncTokens(tokens: Map<string, Token>): void {
    // Add/update tokens
    for (const [id, token] of tokens) {
      const existing = this.renderers.get(id);

      if (existing) {
        existing.update(token);
      } else {
        this.addRenderer(token);
      }
    }

    // Remove deleted tokens
    for (const id of this.renderers.keys()) {
      if (!tokens.has(id)) {
        this.removeRenderer(id);
      }
    }
  }

  /**
   * Create a new token renderer
   */
  private addRenderer(token: Token): void {
    const renderer = new TokenRenderer(token, this.gridSize);

    // Wire up callbacks
    renderer.onClick = (tokenId, shiftKey) => {
      useTokenStore.getState().selectToken(tokenId, shiftKey);
    };

    renderer.onDragEnd = (tokenId, x, y) => {
      useTokenStore.getState().moveToken(tokenId, { x, y }, true);
    };

    this.renderers.set(token.id, renderer);
    this.container.addChild(renderer.container);

    // Set z-index based on y position (tokens lower on screen render on top)
    renderer.container.zIndex = token.position.y;
  }

  /**
   * Remove a token renderer
   */
  private removeRenderer(id: string): void {
    const renderer = this.renderers.get(id);
    if (renderer) {
      this.container.removeChild(renderer.container);
      renderer.destroy();
      this.renderers.delete(id);
    }
  }

  /**
   * Update grid size for all tokens
   */
  setGridSize(size: number): void {
    this.gridSize = size;
    useTokenStore.getState().setGridSize(size);

    for (const renderer of this.renderers.values()) {
      renderer.setGridSize(size);
    }
  }

  /**
   * Handle canvas click (deselect all)
   */
  handleCanvasClick(): void {
    useTokenStore.getState().clearSelection();
  }

  /**
   * Handle selection rectangle
   */
  handleSelectionRect(rect: { x: number; y: number; width: number; height: number }): void {
    useTokenStore.getState().selectTokensInRect(rect);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.unsubscribe?.();

    for (const renderer of this.renderers.values()) {
      renderer.destroy();
    }
    this.renderers.clear();

    this.container.destroy({ children: true });
  }
}
